/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import dayjs from "dayjs";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useDropLocations } from "../../store/useDropLocations";
import { useMarketPricing } from "../../store/useMarketPricing";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import InformationIcon from "../../assets/icons/info/information.png";
import {
  sortLocationNames,
  getOrderedLabelsFromFilterPlaces,
} from "../../utils/locationOrdering";
import {
  getMaterialColor,
  getMaterialContrastColor,
} from "../../constants/plasticColors";
import axios from "axios";

interface MaterialsProps {
  filterType: "location" | "city";
  filterPlaces: string[];
  sequenceStep?: number;
  setSequenceStep?: (n: number) => void;
  sequenceStepIndex?: number;
}

export const Materials = React.memo(
  ({
    filterType,
    filterPlaces,
    sequenceStep,
    setSequenceStep,
    sequenceStepIndex,
  }: MaterialsProps) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const {
      dropLocations,
      getDropLocations,
      locationMaterials,
      getLocationMaterials,
      isLocationMaterialsLoading,
    } = useDropLocations();
    const { marketPrices, getAllMarketPrices } = useMarketPricing();

    const [_, takeScreenshot] = useScreenshot();
    /** State - default: last 12 months */
    const init12Months = () => {
      const end = dayjs();
      const start = end.subtract(12, "month");
      return { start, end };
    };
    const [fromYear, setFromYear] = useState(() => init12Months().start.year());
    const [toYear, setToYear] = useState(() => init12Months().end.year());
    const [fromQuarter, setFromQuarter] = useState(() =>
      Math.ceil((init12Months().start.month() + 1) / 3)
    );
    const [toQuarter, setToQuarter] = useState(() =>
      Math.ceil((init12Months().end.month() + 1) / 3)
    );
    const [fromMonth, setFromMonth] = useState(
      () => init12Months().start.month() + 1
    );
    const [toMonth, setToMonth] = useState(
      () => init12Months().end.month() + 1
    );
    const [fromWeek, setFromWeek] = useState(() =>
      init12Months().start.isoWeek()
    );
    const [toWeek, setToWeek] = useState(() => init12Months().end.isoWeek());
    const [period, setPeriod] = useState<"week" | "month" | "quarter" | "year">(
      "month"
    );
    const [dateValue, setDateValue] = useState<any>({
      fromYear: new Date().getFullYear(),
      toYear: new Date().getFullYear(),
    });
    const [weeklyRange, setWeeklyRange] = useState(new Date());
    const [monthRange, setMonthRange] = useState(new Date());
    const [quarter, setQuarter] = useState<"Q1" | "Q2" | "Q3" | "Q4">("Q1");
    const [isCsvDownloading, setIsCsvDownloading] = useState<boolean>(false);
    /** Callbacks */
    const selectFromYear = (selectedYear: number) => {
      setFromYear(selectedYear);
      setTimeout(() => setRequestKey((prev) => prev + 1), 0); // Force request after state update
    };
    const selectToYear = (selectedYear: number) => {
      setToYear(selectedYear);
      setTimeout(() => setRequestKey((prev) => prev + 1), 0); // Force request after state update
    };
    const selectFromQuarter = (selectedQuarter: number) => {
      setFromQuarter(selectedQuarter);
      setTimeout(() => setRequestKey((prev) => prev + 1), 0); // Force request after state update
    };
    const selectToQuarter = (selectedQuarter: number) => {
      setToQuarter(selectedQuarter);
      setTimeout(() => setRequestKey((prev) => prev + 1), 0); // Force request after state update
    };
    const selectFromMonth = (selectedMonth: number) => {
      setFromMonth(selectedMonth);
      setTimeout(() => setRequestKey((prev) => prev + 1), 0); // Force request after state update
    };
    const selectToMonth = (selectedMonth: number) => {
      setToMonth(selectedMonth);
      setTimeout(() => setRequestKey((prev) => prev + 1), 0); // Force request after state update
    };
    const selectFromWeek = (selectedWeek: number) => {
      setFromWeek(selectedWeek);
      setTimeout(() => setRequestKey((prev) => prev + 1), 0); // Force request after state update
    };
    const selectToWeek = (selectedWeek: number) => {
      setToWeek(selectedWeek);
      setTimeout(() => setRequestKey((prev) => prev + 1), 0); // Force request after state update
    };

    const currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1;
    const currentQuarter = Math.ceil(currentMonth / 3);

    /** Year options */
    const yearOptions = Array.from(
      { length: currentYear - 2023 },
      (_, i) => currentYear - i
    );

    /** Quarter options - disable future quarters for current year */
    const getQuarterOptions = (year: number) =>
      [1, 2, 3, 4].map((q) => ({
        value: q,
        label: `Q${q}`,
        disabled: year === currentYear && q > currentQuarter,
      }));

    /** Month options - disable future months for current year */
    const getMonthOptions = (year: number) =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: dayjs().month(i).format("MMM"),
        disabled: year === currentYear && i + 1 > currentMonth,
      }));

    /** Week options */
    const getWeekOptions = (year: number) => {
      const lastDayOfYear = dayjs(`${year}-12-31`);
      const totalWeeks =
        lastDayOfYear.isoWeek() === 1 ? 52 : lastDayOfYear.isoWeek();
      const currentWeek = dayjs().isoWeek();
      const currentYear = dayjs().year();

      return Array.from({ length: totalWeeks }, (_, i) => {
        const weekNumber = i + 1;
        const startOfWeek = dayjs()
          .year(year)
          .isoWeek(weekNumber)
          .startOf("isoWeek");
        const endOfWeek = dayjs()
          .year(year)
          .isoWeek(weekNumber)
          .endOf("isoWeek");

        return {
          value: weekNumber,
          label: `Week ${weekNumber}: ${startOfWeek.format("MMM DD")} - ${endOfWeek.format("MMM DD")}`,
          disabled: year === currentYear && weekNumber > currentWeek,
        };
      });
    };

    const fromWeekOptions = useMemo(() => getWeekOptions(fromYear), [fromYear]);
    const toWeekOptions = useMemo(() => getWeekOptions(toYear), [toYear]);

    /** Calculate dateValue based on period */
    const calculateDateValue = () => {
      switch (period) {
        case "year":
          return { fromYear, toYear };
        case "month":
          return { fromYear, fromMonth, toYear, toMonth };
        case "quarter":
          return { fromYear, fromQuarter, toYear, toQuarter };
        case "week":
          return { fromYear, fromWeek, toYear, toWeek };
        default:
          return { fromYear, toYear };
      }
    };

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "materials.png";
        link.click();
      });
    };

    const downloadCsv = async () => {
      setIsCsvDownloading(true);
      try {
        let requestBody: any = {
          type: filterType,
          places: filterPlaces,
          period,
        };

        requestBody.fromYear = fromYear;
        requestBody.toYear = toYear;

        if (period === "year") {
        } else if (period === "month") {
          requestBody.fromMonth = fromMonth;
          requestBody.toMonth = toMonth;
        } else if (period === "quarter") {
          requestBody.fromQuarter = fromQuarter;
          requestBody.toQuarter = toQuarter;
        } else if (period === "week") {
          requestBody.fromWeek = fromWeek;
          requestBody.toWeek = toWeek;
        }

        const response = await axios.post(
          "locations/stats/materials/csv",
          requestBody,
          {
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let filename = "materials_stats_daily.csv";
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading CSV:", error);
        // You might want to show an error toast/alert here
      } finally {
        setIsCsvDownloading(false);
      }
    };

    const SpinnerIcon = ({ className = "w-8 h-8" }) => (
      <svg
        aria-hidden="true"
        className={`${className} text-gray-200 animate-spin dark:text-gray-600 fill-primary-base`}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    );

    // Large spinner component for graph area
    const LargeSpinner = () => (
      <div className="flex items-center justify-center h-[300px] w-full">
        <SpinnerIcon />
      </div>
    );

    // Helper function to get material name by ID (for tooltip/legend display)
    const getMaterialNameById = (materialId: string) => {
      if (!materialId) return materialId;
      const idStr = String(materialId);
      const marketPrice = marketPrices?.find(
        (price) => price._id && String(price._id) === idStr
      );
      return marketPrice?.name || materialId;
    };

    /** Loading: rely only on store flag; avoid infinite spinner when request failed */
    const showLoading = isLocationMaterialsLoading;

    const effectiveMaterials = locationMaterials;

    // Transform API data to chart format
    const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";
    const { chartData, locationLabels } = useMemo(() => {
      // Don't process data if still loading
      if (isLocationMaterialsLoading && !effectiveMaterials) {
        return {
          locationLabels: [],
          chartData: [],
        };
      }

      if (!effectiveMaterials?.length) {
        return {
          locationLabels: [],
          chartData: [],
        };
      }

      // Extract all unique locations from API data
      const allLocations = new Set<string>();
      effectiveMaterials.forEach((material) => {
        if (material.data && Array.isArray(material.data)) {
          material.data.forEach(([location]) => {
            allLocations.add(location);
          });
        }
      });
      const realFilterPlaces = filterPlaces.filter(
        (p) => p !== TOTAL_ALL_STORES_ID
      );

      let orderedLocations: string[];
      if (realFilterPlaces.length > 0) {
        // Use user-defined order from filterPlaces (chip order / drag-drop)
        const userOrderedLabels = getOrderedLabelsFromFilterPlaces(
          filterPlaces,
          filterType,
          dropLocations,
          TOTAL_ALL_STORES_ID
        );
        const apiLocationsSet = new Set(allLocations);
        orderedLocations = userOrderedLabels.filter((label) =>
          apiLocationsSet.has(label)
        );
        // If label format doesn't match API keys (e.g. "VLS (FS)" vs "VLS-FS"), fall back to API order so we don't show "no data"
        if (orderedLocations.length === 0 && allLocations.size > 0) {
          orderedLocations = sortLocationNames(
            Array.from(allLocations),
            dropLocations
          );
        }
      } else {
        orderedLocations = sortLocationNames(
          Array.from(allLocations),
          dropLocations
        );
      }

      // Append a synthetic "Total all stores" location only when selected in the filter
      const includeTotalAllStores = filterPlaces.includes(TOTAL_ALL_STORES_ID);
      const totalLabel = "Total all stores";
      const orderedLocationsWithTotal = includeTotalAllStores
        ? [...orderedLocations, totalLabel]
        : orderedLocations;

      // Transform API data to chart format with material name mapping
      const transformedData = effectiveMaterials.map((material) => {
        const materialName = getMaterialNameById(material.name); // Map ID to name

        // Precompute total across all stores for this material
        const totalForMaterial =
          material.data && Array.isArray(material.data)
            ? material.data.reduce((sum: number, entry: any) => {
                const value = Array.isArray(entry) ? entry[1] : 0;
                return sum + (typeof value === "number" ? value : 0);
              }, 0)
            : 0;

        const data = orderedLocationsWithTotal.map((location) => {
          if (includeTotalAllStores && location === totalLabel) {
            return totalForMaterial;
          }
          const locationData = material.data?.find(
            ([loc]: [string, number]) => loc === location
          );
          return locationData ? locationData[1] : 0;
        });

        return {
          name: materialName,
          data,
        };
      });

      // Filter out materials with all zero values
      const filteredData = transformedData.filter((material) =>
        material.data.some((value) => value > 0)
      );

      // Reverse the order so first material appears at bottom of stack, last at top
      const reversedData = [...filteredData].reverse();

      return {
        locationLabels: orderedLocationsWithTotal,
        chartData: reversedData,
      };
    }, [
      effectiveMaterials,
      marketPrices,
      isLocationMaterialsLoading,
      dropLocations,
      filterType,
      filterPlaces,
    ]);

    /** Effects */
    useEffect(() => {
      getDropLocations();
      getAllMarketPrices();
    }, []);

    // Update dateValue when period-specific values change
    useEffect(() => {
      setDateValue(calculateDateValue());
    }, [
      period,
      fromYear,
      toYear,
      fromMonth,
      toMonth,
      fromQuarter,
      toQuarter,
      fromWeek,
      toWeek,
    ]);

    const [requestKey, setRequestKey] = useState(0);
    const prevFilterSigRef = useRef<string | null>(null);

    // Fetch data - always fetch all (frontend filters by filterPlaces)
    // When sequenceStep is provided: block when step < index (waiting). When step > index and our filters unchanged, skip (avoid refetch on sequence advance).
    useEffect(() => {
      if (
        sequenceStep !== undefined &&
        setSequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep < sequenceStepIndex
      ) {
        return;
      }
      // Exclude filterPlaces from filterSig: we always fetch all locations and filter/sort in frontend. Avoids duplicate fetch when filterPlaces is set to default after mount and avoids refetch on drag-drop reorder.
      const filterSig = JSON.stringify({
        period,
        filterType,
        fromYear,
        toYear,
        fromMonth,
        toMonth,
        fromQuarter,
        toQuarter,
        fromWeek,
        toWeek,
        requestKey,
      });
      if (
        sequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep > sequenceStepIndex &&
        prevFilterSigRef.current === filterSig
      ) {
        return;
      }
      prevFilterSigRef.current = filterSig;
      let requestBody: any = {
        type: filterType,
        places: [], // Always fetch all; frontend filters by filterPlaces
        period,
        requestKey,
      };

      requestBody.fromYear = fromYear;
      requestBody.toYear = toYear;

      if (period === "year") {
      } else if (period === "month") {
        requestBody.fromMonth = fromMonth;
        requestBody.toMonth = toMonth;
      } else if (period === "quarter") {
        requestBody.fromQuarter = fromQuarter;
        requestBody.toQuarter = toQuarter;
      } else if (period === "week") {
        requestBody.fromWeek = fromWeek;
        requestBody.toWeek = toWeek;
      }

      const promise = getLocationMaterials(requestBody);
      if (
        setSequenceStep != null &&
        sequenceStepIndex != null &&
        sequenceStep === sequenceStepIndex
      ) {
        Promise.resolve(promise).finally(() =>
          setSequenceStep(sequenceStepIndex + 1)
        );
      }
    }, [
      period,
      filterType,
      fromYear,
      toYear,
      fromMonth,
      toMonth,
      fromQuarter,
      toQuarter,
      fromWeek,
      toWeek,
      requestKey,
      sequenceStep,
      setSequenceStep,
      sequenceStepIndex,
    ]);

    return (
      <div className="flex rounded-xl shadow-none flex-col w-full p-2 sm:p-7 bg-white   mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div></div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-start sm:justify-end gap-2 sm:gap-3">
            {/* Period Filter */}
            <Menu>
              <MenuHandler>
                <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                  <ChevronDown className="ml-1 sm:ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {["year", "quarter", "month", "week"].map((periodOption) => (
                  <MenuItem
                    key={periodOption}
                    disabled={period === periodOption}
                    onClick={() =>
                      setPeriod(
                        periodOption as "week" | "month" | "quarter" | "year"
                      )
                    }
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {periodOption.charAt(0).toUpperCase() +
                      periodOption.slice(1)}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            {/* Year Range Filters */}
            {period === "year" && (
              <>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {fromYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((yearOption) => (
                      <MenuItem
                        key={yearOption}
                        disabled={
                          fromYear === yearOption || yearOption > currentYear
                        }
                        onClick={() => selectFromYear(yearOption)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {yearOption}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <span className="text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  to
                </span>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {toYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((yearOption) => (
                      <MenuItem
                        key={yearOption}
                        disabled={
                          toYear === yearOption || yearOption > currentYear
                        }
                        onClick={() => selectToYear(yearOption)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {yearOption}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </>
            )}

            {/* Quarter Range Filters */}
            {period === "quarter" && (
              <>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {fromYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((yearOption) => (
                      <MenuItem
                        key={yearOption}
                        disabled={
                          fromYear === yearOption || yearOption > currentYear
                        }
                        onClick={() => selectFromYear(yearOption)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {yearOption}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {getQuarterOptions(fromYear).find(
                        (q) => q.value === fromQuarter
                      )?.label || "Q1"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {getQuarterOptions(fromYear).map((q) => (
                      <MenuItem
                        key={q.value}
                        disabled={q.disabled || fromQuarter === q.value}
                        onClick={() => selectFromQuarter(q.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {q.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <span className="text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  to
                </span>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {toYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((yearOption) => (
                      <MenuItem
                        key={yearOption}
                        disabled={
                          toYear === yearOption || yearOption > currentYear
                        }
                        onClick={() => selectToYear(yearOption)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {yearOption}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {getQuarterOptions(toYear).find(
                        (q) => q.value === toQuarter
                      )?.label || "Q4"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {getQuarterOptions(toYear).map((q) => (
                      <MenuItem
                        key={q.value}
                        disabled={q.disabled || toQuarter === q.value}
                        onClick={() => selectToQuarter(q.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {q.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </>
            )}

            {/* Month Range Filters */}
            {period === "month" && (
              <>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {fromYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((yearOption) => (
                      <MenuItem
                        key={yearOption}
                        disabled={
                          fromYear === yearOption || yearOption > currentYear
                        }
                        onClick={() => selectFromYear(yearOption)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {yearOption}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {getMonthOptions(fromYear).find(
                        (m) => m.value === fromMonth
                      )?.label || "Month"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {getMonthOptions(fromYear).map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={m.disabled || fromMonth === m.value}
                        onClick={() => selectFromMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <span className="text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  to
                </span>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {toYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((yearOption) => (
                      <MenuItem
                        key={yearOption}
                        disabled={
                          toYear === yearOption || yearOption > currentYear
                        }
                        onClick={() => selectToYear(yearOption)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {yearOption}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {getMonthOptions(toYear).find((m) => m.value === toMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {getMonthOptions(toYear).map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={m.disabled || toMonth === m.value}
                        onClick={() => selectToMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </>
            )}

            {/* Week Range Filters */}
            {period === "week" && (
              <>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {fromYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((yearOption) => (
                      <MenuItem
                        key={yearOption}
                        disabled={
                          fromYear === yearOption || yearOption > currentYear
                        }
                        onClick={() => selectFromYear(yearOption)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {yearOption}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        fromWeekOptions.find((w) => w.value === fromWeek)?.label
                      }
                    >
                      {fromWeekOptions.find((w) => w.value === fromWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none  max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {fromWeekOptions.map((weekOption) => (
                      <MenuItem
                        key={weekOption.value}
                        disabled={
                          weekOption.disabled || fromWeek === weekOption.value
                        }
                        onClick={() => selectFromWeek(weekOption.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {weekOption.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <span className="text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  to
                </span>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {toYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {yearOptions.map((yearOption) => (
                      <MenuItem
                        key={yearOption}
                        disabled={
                          toYear === yearOption || yearOption > currentYear
                        }
                        onClick={() => selectToYear(yearOption)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {yearOption}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        toWeekOptions.find((w) => w.value === toWeek)?.label
                      }
                    >
                      {toWeekOptions.find((w) => w.value === toWeek)?.label ||
                        "Week"}
                      <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {toWeekOptions.map((weekOption) => (
                      <MenuItem
                        key={weekOption.value}
                        disabled={
                          weekOption.disabled || toWeek === weekOption.value
                        }
                        onClick={() => selectToWeek(weekOption.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {weekOption.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </>
            )}

            {/* Download Buttons */}
            {effectiveMaterials && effectiveMaterials.length > 0 && (
              <button
                onClick={downloadCsv}
                disabled={isCsvDownloading}
                className={`bg-droppie-oranje border border-gray-border h-8 px-6 py-0 rounded active:bg-droppie-zalm disabled:bg-sky-light transition ease-linear flex items-center justify-center gap-2 ${
                  isCsvDownloading ? "cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                {isCsvDownloading ? (
                  <>
                    <SpinnerIcon className="w-4 h-4" />
                    <p className="font-satoshi font-medium text-white text-sm">
                      Downloading...
                    </p>
                  </>
                ) : (
                  <p className="font-satoshi font-medium text-white text-sm">
                    Download CSV
                  </p>
                )}
              </button>
            )}
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
              className="!bg-droppie-oranje !border-droppie-oranje"
            />
          </div>
        </div>

        <section ref={snapshotRef} className="mt-4">
          <div className="flex items-center mb-2 sm:mb-12">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Material volumes per store (kg)
            </p>
            <Tooltip
              content="Non-cumulative: Shows material volumes for each selected time period"
              placeholder={undefined}
            >
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>
          <div className="relative min-h-[320px]">
            {chartData && chartData.length > 0 ? (
              <>
                <Chart
                  // @ts-ignore
                  options={{
                    ...options,
                    colors:
                      chartData.length > 0
                        ? chartData.map((s) => {
                            const baseColor = getMaterialColor(s.name);
                            const contrastColor = getMaterialContrastColor(
                              s.name
                            );
                            return function ({
                              dataPointIndex,
                              w,
                            }: {
                              dataPointIndex: number;
                              w: any;
                            }) {
                              const labels = w?.globals?.labels ?? [];
                              const isTotalBar =
                                labels[dataPointIndex] === "Total all stores";
                              return isTotalBar ? contrastColor : baseColor;
                            };
                          })
                        : options.colors,
                    xaxis: {
                      categories: locationLabels,
                      title: {
                        text: "Stores",
                        style: {
                          fontSize: "14px",
                          fontWeight: "bold",
                          fontFamily: "Satoshi, sans-serif",
                        },
                      },
                      labels: {
                        style: {
                          fontSize: "12px",
                          fontFamily: "Satoshi, sans-serif",
                          colors: locationLabels.map((l) =>
                            l === "Total all stores" ? "#c2410c" : "#374151"
                          ),
                        },
                        formatter: (val: string) =>
                          val === "Total all stores" ? `${val} (sum)` : val,
                      },
                    },
                  }}
                  series={chartData}
                  type="bar"
                  height={450}
                />
                {showLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
                    <SpinnerIcon className="w-8 h-8" />
                  </div>
                )}
              </>
            ) : showLoading ? (
              <LargeSpinner />
            ) : (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-gray-500 font-satoshi">
                  No data available for the selected filters
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }
);

const options = {
  chart: {
    type: "bar" as const,
    stacked: true,
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  stroke: {
    show: true,
    width: 2,
    colors: ["#fff"],
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: "55%",
    },
  },
  dataLabels: {
    //Remove this part if you want to remove labels from the top of the bars
    enabled: true,
    style: {
      fontSize: "10px",
      fontFamily: "Satoshi, sans-serif",
      colors: ["#FFFFFF"],
      fontWeight: 500,
    },
    formatter: function (value: number) {
      if (value === 0) return "";
      return new Intl.NumberFormat("nl-NL", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
  },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
      const resolveColor = (idx: number) => {
        const c = w.config.colors?.[idx];
        if (typeof c === "function") {
          return c({ dataPointIndex, w, seriesIndex: idx });
        }
        return c || "#808080";
      };
      const seriesData = series
        .map((seriesValues: any, index: number) => ({
          name: w.globals.seriesNames[index],
          value: seriesValues[dataPointIndex],
          color: resolveColor(index),
        }))
        .filter((item: any) => Number(item.value) > 0)
        .sort((a: any, b: any) => Number(b.value) - Number(a.value));

      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Satoshi, sans-serif; font-size: 12px;">';
      const categories = w.globals.labels?.[dataPointIndex] ?? "";
      tooltipHtml += `${categories}</div>`;

      seriesData.forEach((item: any) => {
        const formattedValue =
          new Intl.NumberFormat("nl-NL", {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          }).format(Number(item.value ?? 0)) + " kg";
        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${item.color || "#808080"};"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Satoshi, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
              </div>
            </div>
          </div>
        `;
      });

      return tooltipHtml;
    },
  },
  grid: {
    strokeDashArray: 5,
  },
  legend: {
    position: "right",
    offsetY: 0,
    height: 300,
    fontSize: "14px",
    fontFamily: "Satoshi, sans-serif",
    labels: {
      colors: "#374151",
    },
    markers: {
      width: 12,
      height: 12,
      strokeWidth: 0,
      radius: 6,
    },
    itemMargin: {
      horizontal: 0,
      vertical: 4,
    },
  },
  yaxis: {
    title: {
      text: "Materials (kg)",
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    labels: {
      formatter: function (value: number) {
        return (
          new Intl.NumberFormat("nl-NL", {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          }).format(value) + " kg"
        );
      },
    },
  },
  colors: [
    "#5c4d7d", // Textiles
    "#b91c3c", // Ewaste
    "#1d6fd9", // Plastic verpakkingen
    "#8b6fd4", // Oils
    "#0d5c63", // Statie
    "#0d9488", // Melkflessen
    "#047857", // Cans
    "#c2410c", // Total
    "#808080", // Fallback
  ],
  // Prevent ApexCharts "Cannot read properties of undefined (reading 'images')" when annotations is missing
  annotations: {
    images: [],
    points: [],
    xaxis: [],
    yaxis: [],
  },
};
